import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useDataSubjectsQuery } from "../../common/hooks/useSelectableItemQueries";
import { NumberOfDataSubjectsEnum, NumberOfProcessorsEnum, SelectableSecurityMeasure } from "../ProcessingActivity.types";
import { IDataSubjectStep } from "./DataSubjectStep";

import { useDotLegalQuery, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";

export function useDataSubjectStepHook(props: IDataSubjectStep) {
    const { id } = useParams<{ id: string }>();
    const [showAddNewSecurityMeasure, setShowAddNewSecurityMeasure] = useState(false);
    const [showAddNewDataSubject, setShowAddNewDataSubject] = useState(false);
    const [typedSecurityMeasure, setTypedSecurityMeasure] = useState("");
    const [typedDataSubject, setTypedDataSubject] = useState("");
    const { permissions } = useUserContext();

    const dataSubjectsRef = useRef<HTMLTextAreaElement>();
    const dataProcessorsRef = useRef<HTMLTextAreaElement>();

    let dataSubjectsQuery = useDataSubjectsQuery();
    let securityMeasuresQuery = useDotLegalQueryWithKey<Array<SelectableSecurityMeasure>>(
        "processingActivitySecurityMeasures" + id,
        `/securitymeasures/${id}/processingactivity`,
        {
            enabled: permissions.processingActivityDataSubjectPermissions.hasSecurityMeasurePermission,
        }
    );

    useEffect(() => {
        if (props.subjects == NumberOfDataSubjectsEnum.UserEnteredNumberOfSubjects) {
            dataSubjectsRef.current?.focus();
        }
    }, [props.subjects]);

    useEffect(() => {
        if (props.processors == NumberOfProcessorsEnum.UserEnteredNumberOfProcessors) {
            dataProcessorsRef.current?.focus();
        }
    }, [props.processors]);

    let dataSubjectsLoading = dataSubjectsQuery.isLoading;
    let securityMeasuresLoading = securityMeasuresQuery.isLoading;

    let dataSubjecstData;
    if (dataSubjectsQuery.data) {
        dataSubjecstData = dataSubjectsQuery.data.map((d, index) => {
            return {
                name: d.name,
                id: d.id,
            };
        });
    }

    let securityMeasuresData;
    if (securityMeasuresQuery.data) {
        securityMeasuresData = securityMeasuresQuery.data.map((d, index) => {
            return {
                name: d.name,
                id: d.id,
                isInherited: d.isInherited,
                description: d.description,
                isMet: d.isMet,
                isGlobal: d.isGlobal,
                derivedFromSystems: d.derivedFromSystems,
            };
        });
    }

    const handleOnChangeDataSubject = async (dataSubjects: Array<string>) => {
        props.onDataSubjectsChange(dataSubjects);
    };

    const onSaveSecurityMeasure = (id: any, name: string) => {
        let temp = [...props.securityMeasures!];
        temp.push({ securityMeasureId: id.id, isMet: true, derivedFromSystem: [], isGlobal: false });
        props.onSecurityMeasureChange(temp);

        let tempData = [...securityMeasuresQuery.data!];
        tempData.push({ id: id.id, isInherited: false, name: name, description: "", isMet: false, isGlobal: false, derivedFromSystems: [] });
        securityMeasuresQuery.data = tempData;

        securityMeasuresQuery.refetch();
        setTypedSecurityMeasure("");
    };

    const onSaveDataSubject = (reponse: any, name: string) => {
        let temp = [...props.dataSubjects];
        temp.push(reponse.id);
        props.onDataSubjectsChange(temp);

        let tempData = [...dataSubjectsQuery.data!];
        tempData.push({ id: reponse.id, name: name });
        dataSubjectsQuery.data = tempData;

        dataSubjectsQuery.refetch();
        setTypedDataSubject("");
    };

    return {
        dataSubjecstData,
        dataSubjectsLoading,
        securityMeasuresData,
        securityMeasuresLoading,
        showAddNewSecurityMeasure,
        setShowAddNewSecurityMeasure,
        typedSecurityMeasure,
        setTypedSecurityMeasure,
        onSaveSecurityMeasure,
        showAddNewDataSubject,
        setShowAddNewDataSubject,
        typedDataSubject,
        setTypedDataSubject,
        onSaveDataSubject,
        handleOnChangeDataSubject,
        getNoOfDataSubjectSelectOptions,
        getNoOfProcessorsSelectOptions,
        dataSubjectsRef,
        dataProcessorsRef,
        shouldShowCustomDataSubjectInput,
        shouldShowCustomDataProcessorInput,
    };

    function shouldShowCustomDataSubjectInput() {
        // If the user has normal data subject access, then NumberOfDataSubjectsEnum should be UserEnteredNumberOfSubjects before showing custom input
        if (
            props.subjects === NumberOfDataSubjectsEnum.UserEnteredNumberOfSubjects &&
            permissions.processingActivityDataSubjectPermissions.hasDataSubjectsPermission
        ) {
            return true;
        }

        // If the user only has access to custom data subject input and not the normal permission, then we should display the custom input
        if (
            permissions.processingActivityDataSubjectPermissions.hasDataSubjectCustomOnlyPermission &&
            !permissions.processingActivityDataSubjectPermissions.hasDataSubjectsPermission
        ) {
            return true;
        }

        return false;
    }

    function shouldShowCustomDataProcessorInput() {
        // If the user has normal data processor access, then NumberOfProcessorsEnum should be UserEnteredNumberOfProcessors before showing custom input
        if (
            props.processors === NumberOfProcessorsEnum.UserEnteredNumberOfProcessors &&
            permissions.processingActivityDataSubjectPermissions?.hasProcessorsPermission
        ) {
            return true;
        }

        // If the user only has access to custom data processor input and not the normal permission, then we should display the custom input
        if (
            permissions.processingActivityDataSubjectPermissions.hasDataProcessorCustomOnlyPermission &&
            !permissions.processingActivityDataSubjectPermissions?.hasProcessorsPermission
        ) {
            return true;
        }

        return false;
    }
}

export function getNoOfDataSubjectSelectOptions(translateDataSubjectsEnum: (key: string, interpolation?: any) => string) {
    let options = [
        {
            id: NumberOfDataSubjectsEnum.OneToOneHundred.toString(),
            name: translateDataSubjectsEnum(NumberOfDataSubjectsEnum.OneToOneHundred.toString()),
        },
        {
            id: NumberOfDataSubjectsEnum.OneHundredAndOneToOneThousand.toString(),
            name: translateDataSubjectsEnum(NumberOfDataSubjectsEnum.OneHundredAndOneToOneThousand.toString()),
        },
        {
            id: NumberOfDataSubjectsEnum.OneThousandAndOneToTenThousand.toString(),
            name: translateDataSubjectsEnum(NumberOfDataSubjectsEnum.OneThousandAndOneToTenThousand.toString()),
        },
        {
            id: NumberOfDataSubjectsEnum.TenThousandAndOneToOneHundredThousand.toString(),
            name: translateDataSubjectsEnum(NumberOfDataSubjectsEnum.TenThousandAndOneToOneHundredThousand.toString()),
        },
        {
            id: NumberOfDataSubjectsEnum.OneHundredThousandAndOneToOneMillion.toString(),
            name: translateDataSubjectsEnum(NumberOfDataSubjectsEnum.OneHundredThousandAndOneToOneMillion.toString()),
        },
        {
            id: NumberOfDataSubjectsEnum.OneMillionAndOneToInfinite.toString(),
            name: translateDataSubjectsEnum(NumberOfDataSubjectsEnum.OneMillionAndOneToInfinite.toString()),
        },
        {
            id: NumberOfDataSubjectsEnum.UserEnteredNumberOfSubjects.toString(),
            name: translateDataSubjectsEnum(NumberOfDataSubjectsEnum.UserEnteredNumberOfSubjects.toString()),
        },
    ];

    return options;
}
export function getNoOfProcessorsSelectOptions(translateProcessorsEnum: (key: string, interpolation?: any) => string) {
    let options = [
        {
            id: NumberOfProcessorsEnum.OneToTen.toString(),
            name: translateProcessorsEnum(NumberOfProcessorsEnum.OneToTen.toString()),
        },
        {
            id: NumberOfProcessorsEnum.ElevenToFifty.toString(),
            name: translateProcessorsEnum(NumberOfProcessorsEnum.ElevenToFifty.toString()),
        },
        {
            id: NumberOfProcessorsEnum.FiftyOneToOneHundred.toString(),
            name: translateProcessorsEnum(NumberOfProcessorsEnum.FiftyOneToOneHundred.toString()),
        },
        {
            id: NumberOfProcessorsEnum.OneHundredAndOneToOneThousand.toString(),
            name: translateProcessorsEnum(NumberOfProcessorsEnum.OneHundredAndOneToOneThousand.toString()),
        },
        {
            id: NumberOfProcessorsEnum.OneThousandAndOneToInfinite.toString(),
            name: translateProcessorsEnum(NumberOfProcessorsEnum.OneThousandAndOneToInfinite.toString()),
        },
        {
            id: NumberOfProcessorsEnum.UserEnteredNumberOfProcessors.toString(),
            name: translateProcessorsEnum(NumberOfProcessorsEnum.UserEnteredNumberOfProcessors.toString()),
        },
    ];

    return options;
}
